import { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { useTheme } from '@emotion/react'
import * as yup from 'yup'
import { Formik, Form } from 'formik'
import { Alert, Button } from 'chaoskit/src/components'
import FormikField from 'chaoskit/src/components/FormikField'
import { misc } from 'chaoskit/src/assets/styles/utility'
import qs from 'qs'
import to from 'await-to-js'

import TextLink from '~components/TextLink'
import track from '~helpers/track'

const slackSchema = yup.object().shape({
  'bot-field': yup.string().max(0),
  email: yup
    .string()
    .required()
    .email('Please provide a valid email')
    .trim()
    .label('Email'),
})

const SlackSignUpForm = ({ inline, label = 'Join' }) => {
  const theme = useTheme()

  const [response, setResponse] = useState()

  const formId = 'slack'

  const getInitialValues = () => {
    const initialValues = {
      'form-name': formId,
      'bot-field': '',
      email: '',
    }

    // Check for SSR
    if (typeof document !== 'undefined' && document.referrer) {
      initialValues.referrer = document.referrer
    }

    let params = {}

    // Check for SSR
    if (typeof window !== 'undefined') {
      // If we have query params, add them to inputs that will be sent to Netlify
      params = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      })
    }

    return {
      ...initialValues,
      ...params,
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setResponse(null)
    setSubmitting(true)

    const [, success] = await to(
      axios({
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: qs.stringify(values),
        url: '/',
      })
    )

    setSubmitting(false)

    if (success) {
      setResponse('success')

      track('Slack invite requested', {
        label: `netlify-${formId}`,
      })
    } else {
      setResponse('error')
    }
  }

  return (
    <Fragment>
      {response === 'success' && (
        <Alert type="primary">
          Thanks! Here's the link to{' '}
          <TextLink href="https://www.gremlin.com/slack-invite">
            join the Chaos Engineering Slack
          </TextLink>
          . The link has also been sent to your email.
        </Alert>
      )}

      {response === 'error' && (
        <Alert type="danger">
          There was an error submitting your sign up. Please try again later.
        </Alert>
      )}
      {!response && (
        <Formik
          initialValues={getInitialValues()}
          validationSchema={slackSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form
              name={formId}
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              noValidate
            >
              <input type="hidden" name="form-name" value={formId} />
              <FormikField
                name="bot-field"
                css={misc.hide}
                as="input"
                autoComplete="new-password"
              />
              <div
                css={{
                  display: 'grid',
                  gap: theme.space.large,

                  [theme.mq.medium]: {
                    gridTemplateColumns: inline && '1fr auto',
                  },
                }}
              >
                <div>
                  <FormikField
                    required
                    name="email"
                    as="input"
                    type="email"
                    placeholder="Email Address"
                    noContrast
                  />
                </div>
                <div
                  css={{
                    [theme.mq.small__max]: {
                      textAlign: 'center',
                    },
                  }}
                >
                  <Button
                    loading={isSubmitting}
                    actionType="submit"
                    type="primary"
                    noContrast
                  >
                    {label}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Fragment>
  )
}

SlackSignUpForm.propTypes = {
  inline: PropTypes.bool,
  label: PropTypes.string,
}

export default SlackSignUpForm
